import { t } from "@lingui/macro";
import { useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  ExportIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  WalletIcon,
  CheckCircleIcon,
  ClockIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { usePayments } from "@/client/services/payment/payments";
import { useFilterPayment } from "@/client/stores/filter";
import { useDialog } from "@/client/stores/dialog";
import { useExportPayment } from "@/client/services/payment/export-payment";
import qs from "qs";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";

export const PaymentAdminPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "0" | "1" | "2">("all");

  const { filter, replaceFilter, resetFilter } = useFilterPayment();
  const { payments, error, loading } = usePayments(page, filter);
  const { open } = useDialog("filter-payment");
  const { open: openGetPayment } = useDialog<{ id: string }>("get-payment");
  const { refetch } = useExportPayment(qs.stringify(filter));

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecficpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter = { ...filter };
    delete nextFilter.reference;
    delete nextFilter.status;

    if (search.trim()) {
      nextFilter.reference = { contains: search.trim() };
    }
    if (status !== "all") {
      nextFilter.status = { equals: Number(status) };
    }

    setPage(1);
    replaceFilter(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setPage(1);
    resetFilter();
  };

  // Fonctions de formatage et badges (reprises de PaymentTable)
  const formatDate = (date?: string | Date) => {
    if (!date) return "/";
    return new Date(date).toLocaleDateString();
  };

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return <Badge variant="warning">En attente</Badge>;
      case 1:
        return <Badge variant="success">Payé</Badge>;
      case 2:
        return <Badge variant="error">Annulé</Badge>;
      default:
        return (
          <Badge className="border-0 bg-admin-surface-hover text-admin-text-secondary">
            Inconnu
          </Badge>
        );
    }
  };

  // Colonnes basées EXACTEMENT sur PaymentTable
  const columns: Column<any>[] = [
    {
      key: "reference",
      header: "Reference",
      render: (item) => (
        <span className="font-medium text-admin-text-primary">{item.reference}</span>
      ),
    },
    {
      key: "Amount",
      header: t`Montant`,
      render: (item) => <span className="text-admin-text-secondary">{item.Amount}</span>,
    },
    {
      key: "toAmbassador",
      header: "Commission",
      render: (item) => <span className="text-admin-text-secondary">{item.toAmbassador}</span>,
    },
    {
      key: "user",
      header: "Client",
      render: (item) => <span className="text-admin-text-secondary">{item.user?.name || "/"}</span>,
    },
    {
      key: "coupon",
      header: "Coupon",
      render: (item) => (
        <span className="text-admin-text-secondary">{item.coupon?.code || "N/A"}</span>
      ),
    },
    {
      key: "rate",
      header: t`Tarif`,
      render: (item) => (
        <span className="text-admin-text-secondary">{item.rate?.name || "N/A"}</span>
      ),
    },
    {
      key: "openDate",
      header: "Open Date",
      render: (item) => (
        <span className="text-admin-text-secondary">{formatDate(item.openDate)}</span>
      ),
    },
    {
      key: "closeDate",
      header: "Close Date",
      render: (item) => (
        <span className="text-admin-text-secondary">{formatDate(item.closeDate)}</span>
      ),
    },
    { key: "status", header: t`Statut`, render: (item) => getStatusBadge(item.status) },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <Button
          variant="ghost"
          size="icon"
          className="text-admin-text-secondary hover:text-admin-text-primary"
          onClick={() => openGetPayment("read", { id: "get-payment", item: { id: item.id } })}
        >
          <EyeIcon size={18} />
        </Button>
      ),
    },
  ];

  return (
    <>
      {/* Header de page */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">Paiements</h1>
          <p className="mt-1 text-sm text-admin-text-muted">{t`Tableau de bord / Paiements`}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher des paiements…`}
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Notifications"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="h-8 w-8 overflow-hidden rounded-full bg-admin-primary">
            <img src="/avatar.jpg" alt={t`Utilisateur`} className="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Paiements"
              number={payments?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <WalletIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 4.2%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Payés"
              number={payments?.data?.filter((p) => p.status === 1).length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <CheckCircleIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 3.7%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="En attente"
              number={payments?.data?.filter((p) => p.status === 0).length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="bg-admin-warning/20 text-admin-warning rounded-full p-3">
              <ClockIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-admin-warning text-xs">● Live</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`mis à jour`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Pages totales"
              number={payments?.page?.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <UsersIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-accent">▲ 1.3%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>
      </div>

      {/* Section Filtres et Actions */}
      <div className="mb-8 rounded-xl-admin border border-admin-border bg-admin-surface p-6">
        <div className="flex flex-col flex-wrap justify-between gap-4 lg:flex-row">
          {/* Recherche */}
          <div className="relative min-w-[250px] flex-1">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher par référence…`}
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          {/* Filtres Selects */}
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Statut</span>
              <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
                <SelectTrigger className="h-10 min-w-32 bg-admin-background text-admin-text-secondary">
                  <SelectValue placeholder={t`Tous les statuts`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`Tous les statuts`}</SelectItem>
                  <SelectItem value="0">En attente</SelectItem>
                  <SelectItem value="1">Payé</SelectItem>
                  <SelectItem value="2">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-3">
              <Button
                variant="outline"
                onClick={applyQuickFilters}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Filter
              </Button>
              <Button
                variant="outline"
                onClick={() => open("create", { id: "filter-payment" })}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Advanced filters
              </Button>
              <Button
                variant="ghost"
                onClick={resetFilters}
                className="h-10 text-admin-text-muted hover:text-admin-text-primary"
              >
                Reset
              </Button>
              <Button
                variant="outline"
                onClick={() => refetch()}
                className="h-10 bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
              >
                <ExportIcon size={16} className="mr-2" /> Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau & Pagination */}
      <DataTable
        title=""
        data={payments?.data ?? []}
        columns={columns}
        pageSize={10}
        className="shadow-sm-admin"
      />

      {/* Pagination (Style UserAdminPage) */}
      <div className="my-3 flex flex-row justify-end space-x-3">
        {page > 1 && (
          <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
            <CaretCircleLeftIcon size={32} weight="regular" />
          </Button>
        )}
        {page - 2 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 2)}
            className="rounded-lg"
          >
            {page - 2}
          </Button>
        )}
        {page - 1 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 1)}
            className="rounded-lg"
          >
            {page - 1}
          </Button>
        )}

        <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
          {page}
        </Button>
        {payments && page + 1 <= payments.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 1)}
            className="rounded-lg"
          >
            {page + 1}
          </Button>
        )}
        {payments && page + 2 <= payments.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 2)}
            className="rounded-lg"
          >
            {page + 2}
          </Button>
        )}
        {payments && page < payments.page && (
          <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
            <CaretCircleRightIcon size={32} weight="regular" />
          </Button>
        )}
      </div>
    </>
  );
};
